import moment from 'moment';

import { DATE_FORMAT, DATE_TIME_FORMAT } from '@/config';
import { GenderList, RaceList } from '@/constants/form_inputs';
import { getActiveLicensePlate } from '@/layouts/TabLayout/MasterVehicles/licensePlate.utils';
import {
  AssignedUnitShiftFriendlyStatus,
  CFSEvent,
  IAgency,
  IAssignedUnitShift,
  ICFSCloseReason,
  ICFSLog,
  ICFSMasterName,
  ICFSMasterVehicle,
  IIncidentCode,
  IMasterNamePerson,
  KaseMasterNameInvolvementLabels,
  IPhoneNumber,
  PhoneNumberTypeMap,
  ReceivedBy,
  ReceivedByLabels,
  TSeverity,
} from '@/models';
import type { IUnitShiftResource } from '@/models/unit.shift';
import { formatPersonName as formatNameParts } from '@/utils';
import { formatNarrativeContent } from '@/utils/common';
import { formatDistance } from '@/utils/number';
import { formatPhoneNumber } from '@/utils/pattern';
import {
  formatCityStateZip,
  formatStreetLine,
  getActiveAddress,
} from '@/utils/printAddress';

export const FALLBACK = ' ';

export const safeText = (value: unknown, fallback = FALLBACK): string => {
  const str = String(value ?? '').trim();
  return str ? str : fallback;
};

export const formatPrintedOn = (date: Date) =>
  moment(date).format(DATE_TIME_FORMAT || 'MM/DD/YYYY HH:mm');

export const formatCFSDate = (
  date: string | number | Date | null | undefined,
) => {
  if (!date) return FALLBACK;
  const m = moment(date);
  return m.isValid() ? m.format(DATE_FORMAT || 'MM/DD/YYYY') : FALLBACK;
};

export const formatCFSTime = (
  date: string | number | Date | null | undefined,
) => {
  if (!date) return FALLBACK;
  const m = moment(date);
  return m.isValid() ? m.format('HH:mm') : FALLBACK;
};

export const formatDateTimeFull = (
  date: string | number | Date | null | undefined,
) => {
  if (!date) return FALLBACK;
  const m = moment(date);
  return m.isValid()
    ? m.format(DATE_TIME_FORMAT || 'MM/DD/YYYY HH:mm')
    : FALLBACK;
};

export const getEffectiveTimelineTimestamp = (
  log: Pick<ICFSLog, 'createdAt'> & { overriddenAt?: number | null },
): number | null => {
  if (!log) return null;
  if (log.overriddenAt === null) return null;
  if (typeof log.overriddenAt === 'number' && !Number.isNaN(log.overriddenAt)) {
    return log.overriddenAt;
  }
  const timestamp = new Date(log.createdAt).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
};

export const getTimelineEventTimestamp = (
  logs: ICFSLog[] | undefined,
  event: CFSEvent,
  fallback?: string | Date | null,
): number | null => {
  const matches = (logs || [])
    .filter((log) => log.event === event)
    .map((log) => getEffectiveTimelineTimestamp(log))
    .filter((value): value is number => value !== null)
    .sort((a, b) => b - a);

  if (matches.length > 0) {
    return matches[0];
  }

  if (!fallback) return null;
  const fallbackTs = new Date(fallback).getTime();
  return Number.isNaN(fallbackTs) ? null : fallbackTs;
};

export const formatTimelineEventDate = (
  logs: ICFSLog[] | undefined,
  event: CFSEvent,
  fallback?: string | Date | null,
) => formatCFSDate(getTimelineEventTimestamp(logs, event, fallback));

export const formatTimelineEventTime = (
  logs: ICFSLog[] | undefined,
  event: CFSEvent,
  fallback?: string | Date | null,
) => formatCFSTime(getTimelineEventTimestamp(logs, event, fallback));

export const formatTimelineEventDateTime = (
  logs: ICFSLog[] | undefined,
  event: CFSEvent,
  fallback?: string | Date | null,
) => formatDateTimeFull(getTimelineEventTimestamp(logs, event, fallback));

export const formatReceivedBy = (receivedBy?: ReceivedBy | string) => {
  if (!receivedBy) return FALLBACK;
  return ReceivedByLabels[receivedBy as ReceivedBy] || safeText(receivedBy);
};

export const formatSeverity = (severity?: TSeverity | string) => {
  if (!severity) return FALLBACK;
  return safeText(severity);
};

export const formatIncidentCodes = (
  codes: IIncidentCode[] | null | undefined,
) => {
  if (!codes || !codes.length) return FALLBACK;
  return (
    codes
      .map((c: any) => safeText(c.code || c.name || c))
      .filter((v) => v !== FALLBACK)
      .join(', ') || FALLBACK
  );
};

export const formatAddress = (addressInfo: any) => {
  if (!addressInfo?.address) return FALLBACK;
  return safeText(formatStreetLine(addressInfo.address));
};

export const formatAddressCityStateZip = (addressInfo: any) => {
  if (!addressInfo?.address) return FALLBACK;
  return safeText(formatCityStateZip(addressInfo.address));
};

export const formatSubAddress = (addressInfo: any) => {
  if (!addressInfo?.address) return FALLBACK;
  return safeText(addressInfo.address.subAddress);
};

export const formatCrossStreet = (addressInfo: any) => {
  if (!addressInfo?.address) return FALLBACK;
  const { crossStreet1, crossStreet2, distance1, distance2 } =
    addressInfo.address;
  if (crossStreet1 && crossStreet2) {
    return `${crossStreet1}(${formatDistance(
      distance1,
    )}) and ${crossStreet2}(${formatDistance(distance2)})`;
  }
  return FALLBACK;
};

export const formatDisposition = (
  closeReason?: string,
  cfsCloseReasons?: ICFSCloseReason[],
) => {
  if (!closeReason || !cfsCloseReasons?.length) return FALLBACK;
  const closeReasonObject = cfsCloseReasons.find(
    (c) => String(c._id) === closeReason,
  );
  return safeText(closeReasonObject?.reason);
};

export const formatAssignedAgencies = (
  assignedIds: string[] | undefined,
  allAgencies: IAgency[],
) => {
  if (!assignedIds?.length) return FALLBACK;
  const map = new Map(allAgencies.map((a) => [a._id, a.name]));
  return (
    assignedIds
      .map((id) => map.get(id) || FALLBACK)
      .filter((v) => v !== FALLBACK)
      .join(', ') || FALLBACK
  );
};

// --- Unit formatters ---

export const formatUnitName = (unit: IAssignedUnitShift) => {
  const u = unit?.unitShift?.unit;
  return safeText((u as any)?.callSign || (u as any)?.name);
};

export const formatUnitType = (unit: IAssignedUnitShift) => {
  const u = unit?.unitShift?.unit;
  const type = (u as any)?.type;
  return safeText(typeof type === 'string' ? type : type?.name);
};

export const formatUnitStatus = (unit: IAssignedUnitShift) => {
  if (!unit?.status) return FALLBACK;
  return (
    AssignedUnitShiftFriendlyStatus[
      unit.status as keyof typeof AssignedUnitShiftFriendlyStatus
    ] || safeText(unit.status)
  );
};

export const formatUnitEmployees = (unit: IAssignedUnitShift) => {
  const users = unit?.unitShift?.users;
  if (!users?.length) return FALLBACK;
  return users.map((u: IUnitShiftResource) => safeText(u.fullName)).join(', ');
};

export const formatUnitVehicles = (unit: IAssignedUnitShift) => {
  const vehicles = unit?.unitShift?.vehicles;
  if (!vehicles?.length) return FALLBACK;
  return vehicles.map((v: IUnitShiftResource) => safeText(v.name)).join(', ');
};

export const formatUnitEquipment = (unit: IAssignedUnitShift) => {
  const equipments = unit?.unitShift?.equipments;
  if (!equipments?.length) return FALLBACK;
  return equipments.map((e: IUnitShiftResource) => safeText(e.name)).join(', ');
};

// --- Officer formatters ---

export const formatOfficerSerial = (user: IUnitShiftResource) =>
  safeText((user as any)?.profile?.employmentInformation?.badgeNumber);

export const formatOfficerRank = (user: IUnitShiftResource) =>
  safeText(
    (user as any)?.profile?.employmentInformation?.rank || (user as any)?.type,
  );

export const formatOfficerName = (user: IUnitShiftResource) =>
  safeText(formatNameParts(user));

// --- Person (CFS Name) formatters ---

export const formatPersonName = (name: ICFSMasterName) => {
  const mn = name?.masterName as any;
  if (!mn) return FALLBACK;
  if (mn.name) return safeText(mn.name);
  return safeText(formatNameParts(mn));
};

export const formatPersonType = (name: ICFSMasterName) =>
  safeText((name?.masterName as any)?.kind?.replace('MasterName', ''));

export const formatPersonRole = (name: ICFSMasterName) => {
  const involvement = name?.relationship?.involvement;
  if (!involvement?.length) return FALLBACK;

  return (
    involvement
      .map(
        (inv: string) =>
          KaseMasterNameInvolvementLabels[
            inv as keyof typeof KaseMasterNameInvolvementLabels
          ] || inv,
      )
      .join(', ') || FALLBACK
  );
};

export const formatPersonSex = (name: ICFSMasterName) => {
  const person = name?.masterName as IMasterNamePerson | undefined;
  const sex = person?.sex;
  if (!sex) return FALLBACK;
  return GenderList[sex as keyof typeof GenderList];
};

export const formatPersonRace = (name: ICFSMasterName) => {
  const person = name?.masterName as IMasterNamePerson | undefined;
  const race = person?.race;
  if (!race) return FALLBACK;
  return RaceList[race as keyof typeof RaceList];
};

export const formatPersonDOB = (name: ICFSMasterName) => {
  const person = name?.masterName as IMasterNamePerson | undefined;
  return formatCFSDate(person?.birthday);
};

export const formatPersonAge = (name: ICFSMasterName) => {
  const person = name?.masterName as IMasterNamePerson | undefined;
  if (!person?.birthday) return FALLBACK;
  const birth = moment(person.birthday);
  if (!birth.isValid()) return FALLBACK;
  return String(moment().diff(birth, 'years'));
};

export const JUVENILE_AGE_THRESHOLD = 18;

export const isJuvenileName = (name: ICFSMasterName): boolean => {
  const person = name?.masterName as IMasterNamePerson | undefined;
  if (!person?.birthday) return false;
  const birth = moment(person.birthday);
  if (!birth.isValid()) return false;
  return moment().diff(birth, 'years') < JUVENILE_AGE_THRESHOLD;
};

export const formatPersonAddress = (name: ICFSMasterName) => {
  const person = name?.masterName as IMasterNamePerson | undefined;
  if (person?.transient) return 'Transient';
  const addr = getActiveAddress(person);
  if (!addr) return FALLBACK;
  return safeText(formatStreetLine(addr));
};

export const formatPersonCityStateZip = (name: ICFSMasterName) => {
  const person = name?.masterName as IMasterNamePerson | undefined;
  if (person?.transient) return '';
  const addr = getActiveAddress(person);
  if (!addr) return FALLBACK;
  return safeText(formatCityStateZip(addr));
};

export const formatPersonPhone = (name: ICFSMasterName) => {
  const person = name?.masterName as any;
  const phone = person?.phoneNumbers?.[0]?.phoneNumber;
  if (!phone) return FALLBACK;
  return formatPhoneNumber(phone) || FALLBACK;
};

export const getPersonPhoneNumbers = (name: ICFSMasterName): IPhoneNumber[] => {
  const person = name?.masterName as any;
  return person?.phoneNumbers || [];
};

export const formatPhoneTypeLabel = (type: string): string => {
  return (
    PhoneNumberTypeMap[type as keyof typeof PhoneNumberTypeMap] ||
    safeText(type)
  );
};

export const formatPersonPhoneByType = (
  name: ICFSMasterName,
  type: string,
): string => {
  const phones = getPersonPhoneNumbers(name);
  const match = phones.find((p) => p.type === type);
  if (!match) return FALLBACK;
  return formatPhoneNumber(match.phoneNumber) || FALLBACK;
};

// --- Vehicle formatters ---

export const formatVehicleMake = (v: ICFSMasterVehicle) =>
  safeText(v?.masterVehicle?.make);

export const formatVehicleModel = (v: ICFSMasterVehicle) =>
  safeText(v?.masterVehicle?.model);

export const formatVehicleYear = (v: ICFSMasterVehicle) =>
  safeText(v?.masterVehicle?.year);

export const formatVehicleColor = (v: ICFSMasterVehicle) => {
  const color = v?.masterVehicle?.color;
  if (!color) return FALLBACK;
  return safeText(typeof color === 'string' ? color : (color as any)?.name);
};

export const formatVehiclePlate = (v: ICFSMasterVehicle) =>
  safeText(getActiveLicensePlate(v?.masterVehicle)?.number);

export const formatVehiclePlateState = (v: ICFSMasterVehicle) =>
  safeText(getActiveLicensePlate(v?.masterVehicle)?.state);

export const formatVehicleOwner = (v: ICFSMasterVehicle) => {
  const owner = v?.masterVehicle?.owner as any;
  if (!owner) return FALLBACK;
  if (owner.name) return safeText(owner.name);
  return safeText(formatNameParts(owner));
};

// --- Reporter formatters ---

export const formatReporterName = (reporter: any) => {
  if (!reporter) return FALLBACK;
  if (reporter.name) return safeText(reporter.name);
  return safeText(formatNameParts(reporter));
};

export const formatReporterAddress = (reporter: any) => {
  const addr = getActiveAddress(reporter);
  if (!addr) return FALLBACK;
  return safeText(formatStreetLine(addr));
};

export const formatReporterSubAddress = (reporter: any) => {
  const addr = getActiveAddress(reporter);
  if (!addr) return FALLBACK;
  return safeText(addr.subAddress);
};
export const formatReporterCityStateZip = (reporter: any) => {
  const addr = getActiveAddress(reporter);
  if (!addr) return FALLBACK;
  return safeText(formatCityStateZip(addr));
};

export const formatContactPhone = (reporter: any) => {
  if (!reporter?.contactPhoneNumber) return FALLBACK;
  return formatPhoneNumber(reporter.contactPhoneNumber) || FALLBACK;
};

export const formatReporterPhone = (reporter: any) => {
  const phone = reporter?.phoneNumbers?.[0]?.phoneNumber;
  if (!phone) return FALLBACK;
  return formatPhoneNumber(phone) || FALLBACK;
};

// --- Caller formatters ---

export const formatCallerId = (cfs: any, logs: ICFSLog[] | undefined) => {
  const dateTime = formatDateTimeFull(
    getTimelineEventTimestamp(logs, CFSEvent.CREATED, cfs?.createdAt),
  );
  const phone =
    formatPhoneNumber(cfs?.calls?.[0]?.phoneNumber) ||
    formatPhoneNumber(cfs?.reporter?.callerPhoneNumber) ||
    null;
  const callerName: string | null = cfs?.calls?.[0]?.callerName || null;

  const parts = [
    dateTime !== FALLBACK ? dateTime : null,
    phone,
    callerName,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(' - ') : '-';
};

// --- Narrative ---

export const formatNarrativeDate = (dateVal: any): string => {
  if (!dateVal) return FALLBACK;
  return moment(dateVal).format('MM/DD/YYYY HH:mm');
};

export const formatNarrativeCreatedBy = (n: any): string =>
  safeText(n?.createdBy?.fullName);

export const narrativePlainText = (n: any): string => {
  if (!n) return FALLBACK;
  try {
    const content = n.content;
    if (typeof content === 'string') {
      try {
        return safeText(formatNarrativeContent(JSON.parse(content)));
      } catch {
        return safeText(content);
      }
    }
    return safeText(formatNarrativeContent(content));
  } catch {
    return FALLBACK;
  }
};

// --- AdditionalInfo (Description) ---

export const formatDescription = (additionalInfo: any[] | undefined) => {
  if (!additionalInfo?.length) return FALLBACK;
  // Get the latest non-deleted description
  const latest = additionalInfo
    .filter((info: any) => !info.isDeleted)
    .sort(
      (a: any, b: any) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )[0];
  return safeText(latest?.description);
};
